import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { 
  BellIcon, 
  XMarkIcon, 
  ChatBubbleLeftIcon, 
  HeartIcon, 
  UserGroupIcon, 
  UserPlusIcon, 
  CheckCircleIcon, 
  CalendarIcon, 
  ClockIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import useNotificationService from '../../hooks/useNotificationService';
import useWebSocket from '../../hooks/useWebSocket';
import { useAuth } from '../../contexts/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import InitialsAvatar from '../InitialsAvatar';
import UserCardSimple from './UserCardSimple';
import UserCardSimpleLine from './UserCardSimpleLine';

export const NotificationIcon = ({ 
  className = "",
  variant = "topbar", // Can be 'sidebar', 'topbar', or 'badge'
  label = "Notifications", // Label for sidebar variant
  count, // Allow passing count explicitly (useful for the badge variant)
  notificationType = 'all' // Can be 'work', 'engage', 'talk', or 'all'
}) => {
  const {
    notifications,
    unreadCount,
    loading,
    error,
    fetchNotifications,
    markAsSeen,
    deleteNotification,
    getNotificationCountsByType
  } = useNotificationService();
  
  // Make sure we have access to the socket and subscribe function
  const { socket, isConnected, subscribe } = useWebSocket();
  
  // Store service functions in refs to avoid re-renders
  const serviceRef = useRef({
    markAsSeen: null,
    deleteNotification: null,
    fetchNotifications: null
  });
  
  // Update refs when service functions change
  useEffect(() => {
    if (markAsSeen && deleteNotification && fetchNotifications) {
      serviceRef.current = {
        markAsSeen,
        deleteNotification,
        fetchNotifications
      };
    }
  }, [markAsSeen, deleteNotification, fetchNotifications]);
  
  const { user } = useAuth();
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  
  // Update navigate ref when it changes
  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);
  
  const [isOpen, setIsOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [localNotifications, setLocalNotifications] = useState([]);
  const initFetchRef = useRef(false);
  
  // Initial fetch only once when component mounts
  useEffect(() => {
    if (!initFetchRef.current && serviceRef.current.fetchNotifications) {
      initFetchRef.current = true;
      serviceRef.current.fetchNotifications();
    }
  }, []);
  
  // Copy notifications to local state to avoid re-renders
  useEffect(() => {
    if (notifications?.length > 0) {
      setLocalNotifications(notifications);
    }
  }, [notifications]);
  
  // Get notification count based on notificationType
  const getDisplayCount = useCallback(() => {
    // If count is explicitly provided, use it
    if (count !== undefined) return count;
    
    // Otherwise, calculate based on notification type
    if (notificationType === 'all') return unreadCount;
    
    // Get counts by type
    const countsByType = getNotificationCountsByType();
    return countsByType[notificationType] || 0;
  }, [count, notificationType, unreadCount, getNotificationCountsByType]);
  
  // Calculate display count
  const displayCount = getDisplayCount();

  // Define notification type categories
  const WORK_TYPES = ['task_assignment', 'task_response', 'task_update', 'task_mention'];
  const ENGAGE_TYPES = [
    'post_like', 'post_comment', 'post_mention', 'comment_like', 'comment_reply',
    'group_invitation', 'group_join_request', 'group_join_approved', 'group_role_change',
    'poll_vote', 'new_group_post'
  ];
  const TALK_TYPES = ['chat_invitation'];
  const EVENT_TYPES = ['event_mention', 'event_invitation', 'event_response', 'event_update', 'event_cancelled'];

  // Get formatted notification message
  const getFormattedMessage = (notification) => {
    const { type, message, metadata = {}, task } = notification;
    
    // For engage notifications, the IDs are stored differently in the metadata
    const personId = 
      type === 'new_group_post' ? metadata.posterId :
      type.startsWith('post_') ? metadata.posterId || metadata.responderId || metadata.authorId :
      type.startsWith('comment_') ? metadata.responderId || metadata.authorId :
      type === 'group_invitation' ? metadata.inviterId :
      type === 'group_join_request' ? metadata.requesterId :
      type === 'group_join_approved' ? metadata.approverId :
      type === 'task_update' ? metadata.updaterId :
      type === 'task_response' ? metadata.responderId :
      type === 'task_assignment' ? metadata.assignerId :
      metadata.userId || null;
    
    // If we have user information either as personId or in metadata, we can simplify messages
    if (personId || metadata.posterName || metadata.senderName || metadata.responderName || metadata.authorName) {
      // Group notification for new posts
      if (type === 'new_group_post') {
        const groupName = metadata.groupName || 'a group';
        return <span>posted in <span className="font-medium">{groupName}</span></span>;
      }
      
      // Post and comment notifications
      if (type === 'post_like') {
        return <span>liked your post</span>;
      }
      
      if (type === 'post_comment') {
        return <span>commented on your post</span>;
      }
      
      if (type === 'comment_like') {
        return <span>liked your comment</span>;
      }
      
      if (type === 'comment_reply') {
        return <span>replied to your comment</span>;
      }
      
      // Task status change
      if ((type === 'task_update' || type === 'task_response') && 
          (metadata.action === 'status_change' || metadata.subtype === 'status')) {
        const newStatus = metadata.newStatus || 'new status';
        return <span>changed status to <span className="font-medium">{newStatus}</span></span>;
      }
      
      // Task priority change
      if ((type === 'task_update' || type === 'task_response') && 
          (metadata.action === 'priority_change' || metadata.subtype === 'priority')) {
        const newPriority = metadata.newPriority || 'new priority';
        return <span>changed priority to <span className="font-medium">{newPriority}</span></span>;
      }
      
      // Task assignment
      if (type === 'task_assignment') {
        const role = metadata.assignmentRole || 'participant';
        return <span>assigned you as <span className="font-medium">{role}</span> to task</span>;
      }
      
      // Group invitation
      if (type === 'group_invitation') {
        const groupName = metadata.groupName || 'a group';
        return <span>invited you to join <span className="font-medium">{groupName}</span></span>;
      }
      
      // Group join request
      if (type === 'group_join_request') {
        const groupName = metadata.groupName || 'your group';
        return <span>requested to join <span className="font-medium">{groupName}</span></span>;
      }
      
      // Group join approved
      if (type === 'group_join_approved') {
        const groupName = metadata.groupName || 'the group';
        return <span>approved your request to join <span className="font-medium">{groupName}</span></span>;
      }
      
      // Chat invitation
      if (type === 'chat_invitation') {
        return <span>invited you to a chat</span>;
      }
    }
    
    // Fallback to message-based formatting when no user info is available
    
    // Extract user/group names from the message if possible
    if (type === 'new_group_post' && message && message.includes('posted in')) {
      // Message format: "{User} posted in {Group}"
      try {
        const parts = message.split(' posted in ');
        if (parts.length === 2) {
          const [userName, groupName] = parts;
          return (
            <span>
              {personId ? (
                <span>posted in <span className="font-medium">{groupName}</span></span>
              ) : (
                <span>
                  <span className="font-medium">{userName}</span> posted in <span className="font-medium">{groupName}</span>
                </span>
              )}
            </span>
          );
        }
      } catch (e) {
        // Fallback to original message if parsing fails
      }
    }
    
    // Use the original message if we don't have a specific formatter
    return message;
  };

  // Handle notification click with smart navigation
  const handleNotificationClick = async (notification) => {
    // Mark notification as seen
      if (!notification.isSeen) {
      await serviceRef.current.markAsSeen([notification.id]);
    }
    
    const { type, taskId, postId, commentId, groupId } = notification;
    
    // Navigate based on notification type
    if (WORK_TYPES.includes(type)) {
      if (taskId) {
        navigateRef.current(`/work?taskId=${taskId}`);
      } else {
        navigateRef.current('/work');
      }
    } 
    else if (ENGAGE_TYPES.includes(type)) {
      if (type.startsWith('group_') && groupId) {
        navigateRef.current(`/engage/groups/${groupId}`);
      } 
      else if (type === 'new_group_post' && groupId) {
        navigateRef.current(`/engage/groups/${groupId}`);
      }
      else if (type.includes('comment') && postId) {
        navigateRef.current(`/engage/posts/${postId}${commentId ? `?commentId=${commentId}` : ''}`);
      }
      else if (postId) {
        navigateRef.current(`/engage/posts/${postId}`);
      } 
      else {
        navigateRef.current('/engage');
      }
    } 
    else if (TALK_TYPES.includes(type)) {
      if (taskId) {
        navigateRef.current(`/chat?chatId=${taskId}`);
      } else {
        navigateRef.current('/chat');
      }
    }
    else if (EVENT_TYPES.includes(type)) {
      navigateRef.current('/engage/events');
    }
    else {
      // Default fallback
      navigateRef.current('/');
    }

      setIsOpen(false);
  };

  // Get notification icon based on type
  const getNotificationIcon = (type) => {
    if (WORK_TYPES.includes(type)) {
      return <ClockIcon className="h-5 w-5 text-blue-500" />;
    }
    if (type === 'post_like' || type === 'comment_like') {
      return <HeartIcon className="h-5 w-5 text-pink-500" />;
    }
    if (type === 'post_comment' || type === 'comment_reply' || type === 'post_mention') {
      return <ChatBubbleLeftIcon className="h-5 w-5 text-indigo-500" />;
    }
    if (type === 'new_group_post') {
      return <UserGroupIcon className="h-5 w-5 text-teal-500" />;
    }
    if (type.startsWith('group_')) {
      if (type === 'group_invitation') {
        return <UserGroupIcon className="h-5 w-5 text-purple-500" />;
      }
      if (type === 'group_join_request') {
        return <UserPlusIcon className="h-5 w-5 text-green-500" />;
      }
      return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
    }
    if (type.startsWith('event_')) {
      return <CalendarIcon className="h-5 w-5 text-orange-500" />;
    }
    if (TALK_TYPES.includes(type)) {
      return <ChatBubbleLeftIcon className="h-5 w-5 text-green-500" />;
    }
    return <BellIcon className="h-5 w-5 text-gray-500" />;
  };

  // Handle notification delete
  const handleDelete = async (event, notificationId) => {
    event.stopPropagation();
    try {
      // Call the delete API
      const result = await serviceRef.current.deleteNotification(notificationId);
      
      // If successful, update local state immediately
      if (result) {
        // Update localNotifications state
        setLocalNotifications(prevNotifications => 
          prevNotifications.filter(notification => notification.id !== notificationId)
        );
        
        console.log(`Successfully deleted notification: ${notificationId}`);
      } else {
        console.error(`Failed to delete notification: ${notificationId}`);
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  // Handle scroll for infinite loading
  const handleScroll = async (event) => {
    const target = event.target;
    if (target.scrollHeight - target.scrollTop <= target.clientHeight * 1.5 && !loading && hasMore) {
      const nextPage = page + 1;
      const result = await serviceRef.current.fetchNotifications({ page: nextPage });
      if (result && result.notifications.length > 0) {
        setPage(nextPage);
      } else {
        setHasMore(false);
      }
    }
  };

  const toggleNotifications = () => {
    setIsOpen(!isOpen);
    if (!isOpen && serviceRef.current.fetchNotifications) {
      // Only reset and fetch if we're opening the drawer
      setPage(1);
      setHasMore(true);
      
      // Only fetch if we don't already have notifications
      if (localNotifications.length === 0) {
        serviceRef.current.fetchNotifications();
      }
    }
  };

  // Handle drawer open/close
  useEffect(() => {
    if (isOpen && localNotifications.length > 0) {
      const unseenNotifications = localNotifications
        .filter(notification => !notification.isSeen)
        .map(notification => notification.id);

      if (unseenNotifications.length > 0 && serviceRef.current.markAsSeen) {
        serviceRef.current.markAsSeen(unseenNotifications);
      }
    }
    
    // Add body scroll lock when drawer is open
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, localNotifications]);

  const formatNotificationTime = (dateString) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.round(diffMs / 60000);
      const diffHours = Math.round(diffMs / 3600000);
      const diffDays = Math.round(diffMs / 86400000);
      
      // Less than 60 minutes: show as "X min"
      if (diffMins < 60) {
        return `${diffMins}min`;
      } 
      // Less than 24 hours: show as "X hr"
      else if (diffHours < 24) {
        return `${diffHours}hr`;
      } 
      // More than 24 hours: show as date format "DD-MMM"
      else {
        return date.toLocaleDateString('en-US', { 
          day: '2-digit', 
          month: 'short'
        }).replace(' ', '-');
      }
    } catch (error) {
      console.error('Error formatting time:', error);
      return 'Invalid date';
    }
  };

  const renderNotificationItem = (notification) => {
    const { type, message, metadata = {} } = notification;
    
    // Extract sender information from message if not in metadata
    // For example, message format: "User posted in Group"
    let extractedSenderName = null;
    let extractedGroupName = null;
    
    if (type === 'new_group_post' && !metadata.posterName && message) {
      try {
        if (message.includes(' posted in ')) {
          const parts = message.split(' posted in ');
          if (parts.length === 2) {
            extractedSenderName = parts[0];
            extractedGroupName = parts[1];
          }
        }
      } catch (e) {
        console.error('Error parsing message:', e);
      }
    }
    
    // For engage notifications, the IDs are stored differently in the metadata
    const personId = 
      type === 'new_group_post' ? metadata.posterId :
      type.startsWith('post_') ? metadata.posterId || metadata.responderId || metadata.authorId :
      type.startsWith('comment_') ? metadata.responderId || metadata.authorId :
      type === 'group_invitation' ? metadata.inviterId :
      type === 'group_join_request' ? metadata.requesterId :
      type === 'group_join_approved' ? metadata.approverId :
      type === 'task_update' ? metadata.updaterId :
      type === 'task_response' ? metadata.responderId :
      type === 'task_assignment' ? metadata.assignerId :
      metadata.userId || null;
    
    // Get name from metadata if available, or from extracted message
    const engageUserName = 
      type === 'new_group_post' ? metadata.posterName || extractedSenderName :
      type.startsWith('post_') ? metadata.posterName || metadata.senderName || metadata.authorName :
      type.startsWith('comment_') ? metadata.responderName || metadata.authorName :
      type === 'group_invitation' ? metadata.inviterName || metadata.senderName :
      type === 'group_join_request' ? metadata.requesterName :
      type === 'group_join_approved' ? metadata.approverName :
      metadata.userName || null;
    
    // Determine if this is a notification about a group
    const groupInfo = metadata.groupName || extractedGroupName ? {
      id: notification.groupId,
      name: metadata.groupName || extractedGroupName
    } : null;
    
    return (
      <li
        key={notification.id}
        onClick={() => handleNotificationClick(notification)}
        className={`py-3 px-4 hover:bg-gray-50 cursor-pointer relative group ${!notification.isSeen ? 'bg-blue-50/40' : ''}`}
      >
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 mt-0.5">
            {getNotificationIcon(notification.type)}
          </div>
          
          <div className="min-w-0 flex-1 pr-6">
            <div className="flex flex-col space-y-1">
              {/* Show UserCardSimpleLine if we have a personId */}
              {personId ? (
                <UserCardSimpleLine personId={personId} />
              ) : engageUserName ? (
                <div className="text-sm font-medium text-gray-900">{engageUserName}</div>
              ) : null}
              
              <div className="flex justify-between items-center">
                <p className={`text-sm text-gray-900 line-clamp-2 pr-2 ${!notification.isSeen ? 'font-semibold' : ''}`}>
                  {getFormattedMessage(notification)}
                </p>
                
                <p className="text-xs text-gray-500 ml-2 shrink-0 whitespace-nowrap">
                  {formatNotificationTime(notification.createdAt)}
                </p>
              </div>
              
              {notification.task && (
                <p className="text-xs text-gray-600 flex items-center">
                  <span className="line-clamp-1 font-medium">
                    {notification.task.name}
                  </span>
                  {notification.task.status && (
                    <span className="ml-1 px-1.5 py-0.5 text-[10px] rounded bg-blue-100 text-blue-800">
                      {notification.task.status}
                    </span>
                  )}
                </p>
              )}
              
              {groupInfo && !personId && !engageUserName && (
                <p className="text-xs text-gray-600">
                  <span className="font-medium">{groupInfo.name}</span>
                </p>
              )}
            </div>
          </div>
          
          <button
            onClick={(e) => handleDelete(e, notification.id)}
            className="absolute right-4 top-3 opacity-0 group-hover:opacity-100 transition-opacity p-1 text-gray-400 hover:text-red-500"
            aria-label="Delete notification"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
          
          {!notification.isSeen && (
            <div className="absolute right-4 top-3.5 group-hover:opacity-0">
              <div className="h-2 w-2 rounded-full bg-blue-600"></div>
            </div>
          )}
        </div>
      </li>
    );
  };

  const renderDrawer = () => {
    if (!isOpen) return null;

    // Sort notifications by date (latest first)
    const sortedNotifications = [...localNotifications].sort((a, b) => 
      new Date(b.createdAt) - new Date(a.createdAt)
    );

    return createPortal(
      <>
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black/30 z-40 transition-opacity"
          onClick={() => setIsOpen(false)}
        />
        
        {/* Drawer */}
        <div 
          className={`fixed right-0 top-0 h-full w-full sm:w-96 bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out
            ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
        >
          <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200 sticky top-0 z-10">
            <h2 className="text-base font-medium text-gray-900">Notifications</h2>
            <button 
              onClick={() => setIsOpen(false)}
              className="p-1.5 hover:bg-gray-100 rounded-full"
            >
              <XMarkIcon className="size-5 text-gray-500" />
            </button>
          </div>

          <div className="h-[calc(100vh-48px)] overflow-y-auto bg-white" onScroll={handleScroll}>
            {error && (
              <div className="p-3 text-red-500">
                <p className="text-sm">Error loading notifications</p>
                <button 
                  onClick={() => serviceRef.current.fetchNotifications()}
                  className="mt-1 text-sm text-blue-500 hover:text-blue-600"
                >
                  Retry
                </button>
              </div>
            )}

            {localNotifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 p-4 text-center">
                <BellIcon className="h-10 w-10 text-gray-300 mb-2" />
                <p className="text-sm text-gray-500 font-medium">No notifications yet</p>
                <p className="text-xs text-gray-400 mt-1">You're all caught up!</p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {sortedNotifications.map(notification => renderNotificationItem(notification))}
              </ul>
            )}

            {loading && (
              <div className="flex justify-center p-3">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-300 border-t-blue-600" />
              </div>
            )}
          </div>
        </div>
      </>,
      document.body
    );
  };

  const renderNotificationIcon = () => {
    switch (variant) {
      case 'sidebar':
        return (
          <div
            onClick={toggleNotifications}
            className={`group flex items-center gap-3 w-full text-sm font-medium hover:bg-gray-100 cursor-pointer ${className}`}
          >
            <div className="relative flex-shrink-0">
              <BellIcon className="size-5 text-gray-500 hover:text-gray-900" />
              {displayCount > 0 && (
                <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-xs text-white">
                  {displayCount}
                </span>
              )}
            </div>
            <span className="text-gray-900">{label}</span>
          </div>
        );

      case 'topbar':
        return (
          <div
            onClick={toggleNotifications}
            className={`group flex items-center gap-2 text-sm font-medium hover:bg-gray-100 p-2 rounded-full cursor-pointer ${className}`}
          >
            <div className="relative">
              <BellIcon className="size-5 text-gray-500 hover:text-gray-900" />
              {displayCount > 0 && (
                <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-xs text-white">
                  {displayCount}
                </span>
              )}
            </div>
          </div>
        );

      case 'badge':
        return (
          <div className="relative inline-flex">
            {displayCount > 0 && (
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] text-white">
                {displayCount}
              </span>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  // Add socket subscription for deleted notifications
  useEffect(() => {
    if (isConnected && user) {
      const handleNotificationDeleted = ({ notificationId }) => {
        if (!notificationId) return;
        
        console.log('Received notification-deleted event for:', notificationId);
        // Update local notifications state to remove the deleted notification
        setLocalNotifications(prevNotifications => 
          prevNotifications.filter(notification => notification.id !== notificationId)
        );
      };
      
      // Subscribe to notification-deleted events
      const unsubscribeDeleted = subscribe('notification-deleted', handleNotificationDeleted);
      
      return () => {
        unsubscribeDeleted();
      };
    }
  }, [isConnected, user, subscribe]);

  return (
    <>
      {renderNotificationIcon()}
      {renderDrawer()}
    </>
  );
};